<div class="p-4 max-w-[1200px] mx-auto grid gap-5 text-slate-900">

  <!-- ── Steuerung ────────────────────────────────────────────────────────── -->
  <section class="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]">
    <div class="grid gap-4">

      <!-- Standort -->
      <div class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 class="m-0 text-[15px] font-bold">1 · Standort wählen</h2>
          <span class="inline-flex items-center gap-1.5 rounded-full bg-slate-900/5 px-2.5 py-[3px] text-[11px] text-slate-500">
            POST <code>{{ runPath }}</code>
          </span>
        </div>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>Standort</mat-label>
          <mat-select [value]="selectedLocationId()" (valueChange)="setLocationId($event)">
            @for (loc of locationState.locations(); track loc.id) {
              <mat-option [value]="loc.id">{{ loc.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (locationState.locations().length === 0) {
          <div class="text-xs font-semibold text-amber-700">
            Für Ihr Konto sind noch keine Standorte geladen.
          </div>
        } @else {
          <div class="flex flex-wrap gap-4 text-[13px] text-slate-500">
            <span><strong>Name:</strong> {{ selectedLocationName() || '–' }}</span>
            <span><strong>ID:</strong> <code>{{ selectedLocationId() || '–' }}</code></span>
          </div>
        }
      </div>

      <!-- Planungshorizont -->
      <div class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 class="m-0 text-[15px] font-bold">2 · Planungshorizont festlegen</h2>
          <button mat-stroked-button type="button" (click)="resetToNow()" [disabled]="isRunning()">
            <mat-icon>restart_alt</mat-icon>
            Auf jetzt setzen
          </button>
        </div>

        <div class="mb-3.5 flex flex-wrap gap-2">
          @for (preset of horizonPresets; track preset) {
            <button
              type="button"
              class="preset"
              [class.preset-active]="horizonHours() === preset"
              [disabled]="isRunning()"
              (click)="applyHorizon(preset)"
            >
              {{ preset }} h
            </button>
          }
        </div>

        <label class="mb-1.5 block text-[13px] font-semibold" for="optimizer-request">
          Anfrage-JSON
        </label>
        <textarea
          id="optimizer-request"
          class="w-full resize-y rounded-[10px] border border-slate-900/20 bg-[#fbfdfc] p-2.5 font-mono text-xs leading-relaxed"
          [ngModel]="requestJson()"
          (ngModelChange)="requestJson.set($event)"
          rows="6"
          spellcheck="false"
          [disabled]="isRunning()"
        ></textarea>

        @if (!horizonIsValid()) {
          <div class="mt-2 text-xs font-semibold text-amber-700">
            Benötigt gültiges JSON mit <code>startDate</code> und <code>endDate</code>, Ende nach Beginn.
          </div>
        }
      </div>
    </div>

    <!-- Start-Panel -->
    <aside class="g-frame sticky top-4 rounded-2xl p-[2px]">
      <div class="h-full rounded-[14px] bg-white p-[18px]">
        <div class="mb-4 text-[15px] font-bold">3 · Starten</div>

        <button
          type="button"
          class="run-button"
          [class.run-busy]="isRunning()"
          [disabled]="!canRun()"
          (click)="runOptimizer()"
        >
          @if (isRunning()) {
            <span class="run-spinner"></span>
            <span>Läuft…</span>
          } @else {
            <mat-icon>bolt</mat-icon>
            <span>Optimierer starten</span>
          }
        </button>

        @if (!isRunning() && !selectedLocationId()) {
          <div class="mt-3 text-center text-xs text-slate-500">
            Wählen Sie einen Standort, um den Lauf zu starten.
          </div>
        }
        @if (lastDurationMs() > 0 && !isRunning()) {
          <div class="mt-3 text-center text-xs text-slate-500">
            Der letzte Lauf dauerte {{ lastDurationLabel() }}.
          </div>
        }
      </div>
    </aside>
  </section>

  <!-- ── Laufstatus ───────────────────────────────────────────────────────── -->
  @if (isRunning()) {
    <section
      class="rounded-2xl border border-red-600/45 bg-red-600/10 p-4"
      role="status"
      aria-live="polite"
    >
      <div class="flex items-center gap-3.5">
        <span class="pulse-dot h-3.5 w-3.5 flex-none rounded-full bg-red-600"></span>
        <div>
          <div class="font-bold text-red-900">
            Optimierer läuft für {{ selectedLocationName() || 'diesen Standort' }}
          </div>
          <div class="mt-0.5 text-[13px] text-red-900/85">{{ stageLabel() }}</div>
        </div>
        <div class="ml-auto text-[22px] font-bold tabular-nums text-red-800">{{ elapsedLabel() }}</div>
      </div>

      <div class="mt-3.5 h-1.5 overflow-hidden rounded-full bg-red-600/15">
        <span class="sweep block h-full w-[38%] rounded-full"></span>
      </div>

      <div class="mt-2.5 text-xs text-red-900/85">
        @if (isLongRun()) {
          Lange Horizonte können mehrere Minuten dauern. Lassen Sie diesen Tab offen — das Ergebnis
          erscheint hier, sobald der Solver fertig ist.
        } @else {
          {{ horizon()!.hours | number: '1.0-0' }} Stunden Fahrplan werden berechnet. Bitte lassen Sie
          diesen Tab offen.
        }
      </div>
    </section>
  }

  <!-- ── Fehler ───────────────────────────────────────────────────────────── -->
  @if (phase() === 'error' && errorMessage()) {
    <section class="rounded-2xl border border-red-600/40 bg-red-600/[0.07] p-4" role="alert">
      <div class="flex items-center gap-2.5">
        <mat-icon class="text-red-600">error_outline</mat-icon>
        <div class="font-bold text-red-900">Der Optimierungslauf wurde nicht abgeschlossen</div>
        <button mat-button type="button" class="ml-auto" (click)="dismissResult()">Schließen</button>
      </div>
      <pre class="mt-2.5 mb-0 whitespace-pre-wrap break-words text-[13px] text-red-900">{{ errorMessage() }}</pre>
      <div class="mt-2.5 text-xs text-red-900/85">Abgebrochen nach {{ lastDurationLabel() }}.</div>
    </section>
  }

  <!-- ── Erfolg ───────────────────────────────────────────────────────────── -->
  @if (phase() === 'success') {
    <section class="rise rounded-2xl border border-emerald-600/40 bg-emerald-600/[0.07] p-[18px]">
      <div class="flex items-center gap-3">
        <mat-icon class="h-8 w-8 text-[30px] text-emerald-600">check_circle</mat-icon>
        <div>
          <div class="text-base font-bold text-emerald-800">
            Optimierer in {{ lastDurationLabel() }} abgeschlossen
          </div>
          <div class="mt-0.5 text-[13px] text-emerald-800/85">
            {{ response()?.status || 'abgeschlossen' }}
            @if (response()?.terminationStatus) {
              · Solver: {{ response()!.terminationStatus }}
            }
            ·
            @if (response()?.posted) {
              Ergebnis für diesen Standort gespeichert
            } @else {
              Ergebnis wurde nicht an die API zurückgeschrieben
            }
          </div>
        </div>
        <button mat-button type="button" class="ml-auto" (click)="dismissResult()">Schließen</button>
      </div>

      <div class="mt-4 flex flex-wrap items-center gap-2.5">
        <a mat-flat-button color="primary" class="font-semibold" routerLink="/energy-chart">
          <mat-icon>insights</mat-icon>
          Energiediagramm für {{ selectedLocationName() || 'diesen Standort' }} öffnen
        </a>
        <button mat-stroked-button type="button" (click)="copyResult()">
          <mat-icon>content_copy</mat-icon>
          JSON kopieren
        </button>
      </div>

      @if (schedule(); as sched) {
        <div class="mt-4 rounded-[10px] border border-emerald-600/25 bg-white p-3">
          <div class="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[13px]">
            <span class="font-bold text-emerald-800">Neuer Fahrplan</span>
            <span class="text-slate-500">{{ sched.scheduledActions?.length ?? 0 }} Aktionen</span>
            @if (sched.created) {
              <span class="text-slate-500">erstellt {{ sched.created | date: 'dd.MM.yyyy HH:mm' }}</span>
            }
            @if (sched.expiresAt) {
              <span class="text-slate-500">gültig bis {{ sched.expiresAt | date: 'dd.MM.yyyy HH:mm' }}</span>
            }
          </div>
          @if (sched.scheduledActions?.length) {
            <div class="max-h-[260px] overflow-auto">
              <table class="w-full text-left text-xs">
                <thead class="sticky top-0 bg-white text-slate-500">
                  <tr>
                    <th class="py-1 pr-3 font-semibold">Cron</th>
                    <th class="py-1 pr-3 font-semibold">Aktion</th>
                    <th class="py-1 pr-3 font-semibold">Wert</th>
                    <th class="py-1 pr-3 font-semibold">Min SoC</th>
                    <th class="py-1 font-semibold">Max Kauf-SoC</th>
                  </tr>
                </thead>
                <tbody>
                  @for (action of sched.scheduledActions; track action.id ?? $index) {
                    <tr class="border-t border-slate-900/5">
                      <td class="py-1 pr-3 font-mono">{{ action.cron || '–' }}</td>
                      <td class="py-1 pr-3">{{ action.type || '–' }}{{ action.registerName ? ' · ' + action.registerName : '' }}</td>
                      <td class="py-1 pr-3 tabular-nums">{{ action.value ?? '–' }}</td>
                      <td class="py-1 pr-3 tabular-nums">{{ action.minSoC ?? '–' }}</td>
                      <td class="py-1 tabular-nums">{{ action.maxPurchaseSoC ?? '–' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      }
      @if (scheduleError()) {
        <div class="mt-3 text-xs font-semibold text-amber-700">
          Fahrplan konnte nicht geladen werden: {{ scheduleError() }}
        </div>
      }

      <pre class="mt-3 mb-0 max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-[10px] bg-slate-900/5 p-3 text-xs">{{ resultJson() }}</pre>
    </section>
  }

  <!-- ── Läufe dieser Sitzung ─────────────────────────────────────────────── -->
  @if (runLog().length > 0) {
    <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 class="m-0 text-[15px] font-bold">Läufe in dieser Sitzung</h2>
        <button mat-button type="button" (click)="clearLog()">Leeren</button>
      </div>
      <ul class="m-0 grid list-none gap-1.5 p-0">
        @for (entry of runLog(); track entry.id) {
          <li
            class="grid items-baseline gap-2.5 rounded-lg px-2.5 py-2 text-[13px] md:grid-cols-[72px_minmax(90px,1fr)_minmax(0,2fr)_72px]"
            [class]="entry.outcome === 'error' ? 'bg-red-600/[0.07] text-red-900' : 'bg-slate-900/[0.03]'"
          >
            <span class="tabular-nums text-slate-500">{{ entry.startedAt | date: 'HH:mm:ss' }}</span>
            <span class="font-semibold">{{ entry.locationName }}</span>
            <span class="[overflow-wrap:anywhere]">{{ entry.detail }}</span>
            <span class="tabular-nums text-slate-500 md:text-right">{{ formatDuration(entry.durationMs) }}</span>
          </li>
        }
      </ul>
    </section>
  }
</div>
